import { useEffect, useMemo, useState } from 'react'
import ReactTooltip from 'react-tooltip'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import { Info, ArrowLeft } from '@phosphor-icons/react'
import { useNavigate, useLocation, Link } from 'react-router-dom'
import { Formik } from 'formik'
import { useTranslation } from 'react-i18next'

import Dashboard from '../Dashboard'
import Tabs from '../Tabs/Tabs'
import { selectPropertyManagers } from '../../reducers/Organisation/propertyManagersReducer'
import { selectShiftManagers } from '../../reducers/Organisation/shiftManagersReducer'
import { selectStaffPersons } from '../../reducers/maintenanceStaffReducer'
import { selectActiveBuilding } from '../../reducers/buildingSelectorReducer'
import api from '../../api/api'
import { useSnackbar } from '../../helpers/hooks/useSnackbar'
import ToggleField from '../FormLegacy/FormFieldsV2/ToggleField'

import EscalationsWarning from './EscalationsWarning'
import { defaultValues, defaultOption } from './helper'
import OnCallScheduleTab from './OnCallScheduleTab'
import EscalationsTab from './EscalationsTab'

const OnCallSchedule = ({
  propertyManagers,
  shiftManagers,
  staffPeople,
  currentBuildingId,
  yardiPropertyId,
}) => {
  const { t } = useTranslation(['on-call-schedule', 'common'])
  const navigate = useNavigate()
  const location = useLocation()
  const { state } = location
  const { displaySnackbar } = useSnackbar()
  const [initialValues, setInitialValues] = useState(defaultValues)
  const [isConfirmPopupVisible, setConfirmPopupVisible] = useState(false)
  const [escalationSettings, setEscalatiuonSettings] = useState({})
  const [priorities, setPriorities] = useState([])
  const [escalationSwitch, setEscalationSwitch] = useState(escalationSettings.escalationEnabled)
  const isEscalationDisabled = useMemo(
    () => Object.values(initialValues).some((item) => !item.value),
    [initialValues],
  )
  const [activeTab, setActiveTab] = useState(0)

  const fetchCallSchedule = () =>
    api
      .getCallSchedule(state.inboxId)
      .then(({ data }) => {
        const preparedValues = {}
        for (const key in data) {
          const currentItem = data[key]
          preparedValues[key] = currentItem
            ? {
                value: currentItem.email,
                label: `${currentItem.firstName} ${currentItem.lastName}`,
                avatar: currentItem.avatar,
              }
            : defaultOption
        }
        setInitialValues(preparedValues)
      })
      .catch((err) => console.error(err))
  const fetchEscalationSettings = () =>
    api
      .getEscalationSettings(state.inboxId)
      .then(({ data }) => {
        setEscalatiuonSettings(data)
        setEscalationSwitch(data.escalationEnabled)
      })
      .catch((err) => console.error(err))

  const fetchPriorities = () =>
    api
      .getYardiConfig(currentBuildingId)
      .then(({ data }) => {
        setPriorities(
          data.priorities.map((priority) => ({
            label: priority,
            value: priority,
          })),
        )
      })
      .catch((err) => console.error(err))

  useEffect(() => {
    if (!state || !state.inboxId) {
      navigate('/dashboard/chat')
    } else {
      fetchCallSchedule()
      fetchEscalationSettings()
      yardiPropertyId && fetchPriorities()
    }
  }, [activeTab])

  const handleSubmit = (data) => {
    const preparedData = {}
    for (const key in data) {
      preparedData[key] = data[key].value || null
    }
    api
      .updateCallSchedule(state.inboxId, preparedData)
      .then(() => displaySnackbar(t('on-call-schedule:notifications.savedSuccessfully')))
      .catch(() => displaySnackbar(t('on-call-schedule:notifications.saveError'), true))
      .finally(() => {
        fetchCallSchedule()
        fetchEscalationSettings()
      })
  }

  useEffect(() => {
    if (state && state.buildingId && currentBuildingId) {
      currentBuildingId !== state.buildingId && navigate('/dashboard/chat?settings=true')
    }
  }, [currentBuildingId, state])

  const handleSwitchChange = () => {
    setEscalationSwitch((prev) => !prev)
    if (escalationSwitch) {
      api.updateEscalationSettings(state.inboxId, {
        ...escalationSettings,
        escalationEnabled: false,
      })
    }
  }

  return (
    <Dashboard.Container className="on-call-schedule__wrap">
      <div className="on-call-schedule__header">
        <Link to="/dashboard/chat">
          <button className="on-call-schedule__back-btn">
            <ArrowLeft size={20} />
            {t('common:actions.back')}
          </button>
        </Link>
        <h2 className="on-call-schedule__title">{t('on-call-schedule:header.title')}</h2>
      </div>
      <Formik initialValues={initialValues} onSubmit={handleSubmit} enableReinitialize>
        {({ handleSubmit, values, setFieldValue, setValues, dirty }) => (
          <Tabs
            defaultTab={0}
            onTabChange={(id) => {
              if (dirty) {
                setConfirmPopupVisible(id !== 0)
              } else {
                setActiveTab(id)
              }
            }}
            activeTab={activeTab}
            className="on-call-schedule__tabs"
          >
            <Tabs.Panel
              heading={
                <div className="heading_text">
                  {t('on-call-schedule:tabs.onCall.heading')}
                  <div className="heading_text__tip" data-tip data-for="on-call-tip">
                    <Info size={20} />
                    <ReactTooltip id="on-call-tip" place="top" effect="solid">
                      {t('on-call-schedule:tabs.onCall.tooltip')}
                    </ReactTooltip>
                  </div>
                </div>
              }
            >
              <OnCallScheduleTab
                handleSubmit={handleSubmit}
                values={values}
                setFieldValue={setFieldValue}
                setValues={setValues}
                initialValues={initialValues}
                isConfirmPopupVisible={isConfirmPopupVisible}
                setConfirmPopupVisible={setConfirmPopupVisible}
                propertyManagers={propertyManagers}
                shiftManagers={shiftManagers}
                staffPeople={staffPeople}
              />
            </Tabs.Panel>
            <Tabs.Panel
              heading={
                <div className="heading_text">
                  {t('on-call-schedule:tabs.escalations.heading')}
                  <div className="heading_text__tip" data-tip data-for="escalations-tip">
                    <Info size={20} />
                    <ReactTooltip multiline id="escalations-tip" place="top" effect="solid">
                      {t('on-call-schedule:tabs.escalations.tooltip')}
                    </ReactTooltip>
                  </div>
                </div>
              }
            >
              <div className="on-call-schedule__escalations">
                <div className="on-call-schedule__escalations-turn-on">
                  <ToggleField
                    value={escalationSwitch}
                    name="escalations"
                    label={t('on-call-schedule:escalations.turnOnToggle')}
                    labelOnRight
                    onChange={handleSwitchChange}
                    disabled={isEscalationDisabled}
                  />
                </div>
                {escalationSwitch ? (
                  <EscalationsTab
                    staffPeople={staffPeople}
                    propertyManagers={propertyManagers}
                    shiftManagers={shiftManagers}
                    escalationSettings={escalationSettings}
                    inboxId={state.inboxId}
                    priorities={priorities}
                  />
                ) : (
                  <EscalationsWarning onClick={() => setActiveTab(0)} />
                )}
              </div>
            </Tabs.Panel>
          </Tabs>
        )}
      </Formik>
    </Dashboard.Container>
  )
}

OnCallSchedule.propTypes = {
  shiftManagers: PropTypes.array,
  propertyManagers: PropTypes.array,
  staffPeople: PropTypes.array,
  currentBuildingId: PropTypes.number,
  yardiPropertyId: PropTypes.string,
}

export default connect((state) => {
  const currentBuilding = selectActiveBuilding(state)
  const currentBuildingId = currentBuilding ? currentBuilding.id : null
  const yardiPropertyId = currentBuilding ? currentBuilding.yardiPropertyId : null

  return {
    shiftManagers: selectShiftManagers(state),
    propertyManagers: selectPropertyManagers(state),
    staffPeople: selectStaffPersons(state),
    yardiPropertyId,
    currentBuildingId,
  }
}, {})(OnCallSchedule)
